import { SNAPSHOT_RATE, TICK_RATE, type CrateState, type PlayerState, type WorldState } from '../../shared/types';
import { sampleBodyInterval } from './sampling';
import { clamp, mix } from './paint';

export const TICK_MS = 1000 / TICK_RATE;
/** Ticks between two broadcast snapshots (3 at 60 Hz / 20 Hz). */
export const SNAPSHOT_TICKS = TICK_RATE / SNAPSHOT_RATE;
/** Fixed remote delay: one snapshot interval plus a 1.5-tick (25 ms) jitter buffer. */
export const BASE_DELAY_TICKS = SNAPSHOT_TICKS + 1.5;
const MAX_DELAY_TICKS = 9;
/** A slewed clock corrects ordinary drift at ≤ 6 % speed; only a gross error re-anchors it. */
const RESYNC_TICKS = 3;
const SLEW_TICKS = 12;
const FRAME_LIMIT = 24;

/**
 * Where the authoritative tick clock sits on the local performance.now() axis,
 * estimated from snapshot arrival times. Read-only: it never moves a body.
 */
export class SnapshotClock {
  offsetMs = NaN;
  samples = 0;

  reset(): void { this.offsetMs = NaN; this.samples = 0; }

  get ready(): boolean { return Number.isFinite(this.offsetMs); }

  observe(tick: number, receivedAt: number): void {
    const sample = receivedAt - tick * TICK_MS;
    if (!this.ready) { this.offsetMs = sample; this.samples = 1; return; }
    this.offsetMs += clamp(sample - this.offsetMs, -35, 35) * 0.12;
    this.samples++;
  }

  /** Estimated authoritative tick (fractional) at a local time. */
  tickAt(timeMs: number): number { return (timeMs - this.offsetMs) / TICK_MS; }
}

/**
 * A render clock in ticks that only advances. Each frame it moves by the real
 * elapsed time times a rate within [0.94, 1.06] toward its target, so a drifting
 * estimate never produces a frame with zero or double travel, let alone a step back.
 */
export class RenderClock {
  private value = NaN;
  rate = 1;
  resyncs = 0;

  reset(): void { this.value = NaN; this.rate = 1; }

  get tick(): number { return this.value; }

  get ready(): boolean { return Number.isFinite(this.value); }

  advance(target: number, dtMs: number): number {
    if (!Number.isFinite(target)) return this.value;
    if (!this.ready) { this.value = target; this.rate = 1; return this.value; }
    const step = Math.max(0, dtMs) / TICK_MS;
    // Compare the target with where a nominal advance would land, not with last frame's value.
    const error = target - (this.value + step);
    if (Math.abs(error) > RESYNC_TICKS) { this.value = target; this.rate = 1; this.resyncs++; return this.value; }
    this.rate = clamp(1 + error / SLEW_TICKS, 0.94, 1.06);
    this.value += step * this.rate;
    return this.value;
  }

  /** Holding at a ceiling (never extrapolating) must also hold the clock itself, so
   * resuming continues from the held tick instead of jumping to catch up. */
  limit(maximum: number): number {
    if (this.ready && this.value > maximum) this.value = maximum;
    return this.value;
  }

  /** Move forward to a value at once (never backward). */
  set(value: number): number {
    if (Number.isFinite(value) && (!this.ready || value > this.value)) this.value = value;
    return this.value;
  }

  /** Linear estimate of this clock at a nearby local time (stamping input records). */
  at(timeMs: number, frameTimeMs: number): number { return this.value + (timeMs - frameTimeMs) / TICK_MS * this.rate; }
}

export interface RemoteFrame { world: WorldState; tick: number; receivedAt: number }

export interface RemoteSample {
  a: RemoteFrame;
  b: RemoteFrame;
  amount: number;
  tick: number;
  elapsed: number;
  players: PlayerState[];
  crates: CrateState[];
  cornerTransitions: number;
  maxCorrection: number;
}

const activeBody = (p: PlayerState) => p.connected && !p.entered;

/**
 * Fixed-delay interpolation of received snapshots. The render tick sits about one
 * snapshot interval plus a jitter buffer behind the estimated authoritative clock and
 * is clamped to the newest received frame: a late snapshot holds the body still, it
 * never dead-reckons it ahead. The buffer widens when holds happen and relaxes slowly.
 */
export class RemoteTimeline {
  frames: RemoteFrame[] = [];
  delayTicks = BASE_DELAY_TICKS;
  readonly clock = new RenderClock();
  holds = 0;
  private lastSample: RemoteSample | null = null;

  reset(): void { this.frames = []; this.delayTicks = BASE_DELAY_TICKS; this.clock.reset(); this.lastSample = null; }

  get newest(): RemoteFrame | undefined { return this.frames.at(-1); }

  push(frame: RemoteFrame): void {
    const last = this.frames.at(-1);
    if (last && last.tick === frame.tick) this.frames[this.frames.length - 1] = frame;
    else if (last && frame.tick < last.tick) return;
    else this.frames.push(frame);
    if (this.frames.length > FRAME_LIMIT) this.frames.shift();
  }

  /** Advance the remote render clock for this frame. nowTick is the SnapshotClock estimate at frame time. */
  advance(nowTick: number, dtMs: number): number {
    const newest = this.frames.at(-1);
    if (!newest || !Number.isFinite(nowTick)) return NaN;
    let target = nowTick - this.delayTicks;
    if (target > newest.tick) {
      // The clock would pass the newest received state: hold there and widen the buffer.
      this.delayTicks = Math.min(MAX_DELAY_TICKS, this.delayTicks + Math.min(1, (target - newest.tick) * 0.5) + 0.05);
      target = newest.tick;
      this.holds++;
    } else if (this.delayTicks > BASE_DELAY_TICKS) {
      this.delayTicks = Math.max(BASE_DELAY_TICKS, this.delayTicks - dtMs / 1000 * 0.4);
    }
    this.clock.advance(target, dtMs);
    return this.clock.limit(newest.tick);
  }

  sample(tick: number): RemoteSample | null {
    if (!this.frames.length) return null;
    if (!Number.isFinite(tick)) tick = this.frames.at(-1)!.tick;
    let a = this.frames[0];
    let b = a;
    for (const frame of this.frames) {
      if (frame.tick <= tick) { a = frame; b = frame; continue; }
      b = frame;
      break;
    }
    const amount = b.tick === a.tick ? 1 : clamp((tick - a.tick) / (b.tick - a.tick), 0, 1);
    const before = [...a.world.players.filter(activeBody), ...a.world.crates];
    const after = [...b.world.players.filter(activeBody), ...b.world.crates];
    const sampled = sampleBodyInterval<PlayerState | CrateState>(before, after, amount);
    const poses = new Map(sampled.bodies.map(body => [body.id, body]));
    const players = b.world.players.map(player => {
      const previous = a.world.players.find(p => p.id === player.id);
      const pose = poses.get(player.id) ?? player;
      const grounded = player.grounded && (previous?.grounded !== false || amount >= 0.9999);
      return {
        ...player, x: pose.x, y: pose.y, grounded,
        supportId: grounded ? amount < 1 ? previous?.supportId ?? player.supportId : player.supportId : null,
        vx: mix(previous?.vx ?? player.vx, player.vx, amount), vy: mix(previous?.vy ?? player.vy, player.vy, amount),
      };
    });
    const crates = b.world.crates.map(crate => {
      const previous = a.world.crates.find(c => c.id === crate.id);
      const pose = poses.get(crate.id) ?? crate;
      return { ...crate, x: pose.x, y: pose.y, vx: mix(previous?.vx ?? crate.vx, crate.vx, amount), vy: mix(previous?.vy ?? crate.vy, crate.vy, amount) };
    });
    this.lastSample = { a, b, amount, tick, elapsed: mix(a.world.elapsed, b.world.elapsed, amount), players, crates,
      cornerTransitions: sampled.cornerTransitions, maxCorrection: sampled.maxCorrection };
    return this.lastSample;
  }

  diagnostics() {
    return { delayTicks: this.delayTicks, delayMs: this.delayTicks * TICK_MS, bufferedSnapshots: this.frames.length,
      renderTick: this.clock.tick, rate: this.clock.rate, holds: this.holds, resyncs: this.clock.resyncs,
      amount: this.lastSample?.amount ?? 0 };
  }
}
